import React, { useState, useEffect, useRef } from 'react';
import { Package as PackageIcon, Trash2, Pencil, X, Info } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import { toast } from 'react-toastify';
import { uploadImage, getImages } from '../services/uploadService';
import PhotoGallery from './PhotoGallery';
import ConfirmDialog from './ConfirmDialog';
import RichTextEditor from './RichTextEditor';
import SortableList from './SortableList';
import ShimmerLoader from './ShimmerLoader';
import AddItemsModal from './AddItemsModal';
import AddModelsModal from './AddModelsModal';
import AddUnitsModal from './AddUnitsModal';
import ChangeHistoryButton from './ChangeHistoryButton';
import ChangeHistoryDrawer from './ChangeHistoryDrawer';
import { PackageModel } from '../types/packageModel';
import { PackageUnit } from '../types/packageUnit';
import { packageModelService } from '../services/packageModelService';
import { packageUnitService } from '../services/packageUnitService';
import api from '../services/api';
import '../styles/PackageDetailsSlider.css';

interface PackageDetailsSliderProps {
  workspaceId: string;
  packageId: string;
  onClose: () => void;
  onSave?: () => void;
}

interface Package {
  _id: string;
  name: string;
  caption?: string;
  description?: string;
  projectId: string;
  projectName?: string;
  location?: string;
  phase?: string;
  isActive: boolean;
  order: number;
  heroImageId?: string;
  photoIds?: string[];
  spaces?: string[];
  constructionNotes?: string;
  labourCost?: number;
  materialCost?: number;
  materialType?: string;
  size?: string;
  modelCategoryCode?: string;
  finish?: string;
  selectionColor?: string;
  associatedTrades?: string;
  includedItemModels?: string;
  specifications?: string;
  collections?: CollectionReference[];
  parentPackage?: ParentPackageReference;
  includedOptions?: OptionReference[];
  incompatiblePackages?: PackageReference[];
  incompatiblePersonalizationOptions?: OptionReference[];
  createdAt: string;
  updatedAt: string;
}

interface CollectionReference {
  collectionId: string;
  name?: string;
}

interface PackageReference {
  _id: string;
  name?: string;
}

interface OptionReference {
  _id: string;
  name?: string;
}

interface ParentPackageReference {
  id: string;
  name?: string;
}

interface Project {
  _id: string;
  name: string;
}

interface PackageLocation {
  _id: string;
  name: string;
  order: number;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface PurchasedUnit {
  unitNo: string;
  unitId: string;
  unitStatus: string;
  dealNumber: string;
  dealId: string;
  purchasers: Array<{ displayName: string; isPrimary: boolean; contactId: string }>;
  dealStage: string;
}

const PackageDetailsSlider: React.FC<PackageDetailsSliderProps> = ({
  workspaceId,
  packageId,
  onClose,
  onSave
}) => {
  const [pkg, setPkg] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [locations, setLocations] = useState<PackageLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Hero image
  const [uploadingHero, setUploadingHero] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);

  // General info editing
  const [editingGeneralInfo, setEditingGeneralInfo] = useState(false);
  const [generalInfoData, setGeneralInfoData] = useState({
    name: '',
    caption: '',
    description: '',
    location: '',
    phase: '',
    isActive: true,
    order: 0,
    materialType: '',
    size: '',
    modelCategoryCode: '',
    finish: '',
    selectionColor: '',
    associatedTrades: '',
    includedItemModels: '',
    specifications: ''
  });
  const [savingGeneralInfo, setSavingGeneralInfo] = useState(false);

  // Description & Construction Notes editing
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionData, setDescriptionData] = useState({
    description: '',
    constructionNotes: ''
  });
  const [savingDescription, setSavingDescription] = useState(false);

  // Settings editing
  const [editingSettings, setEditingSettings] = useState(false);
  const [settingsData, setSettingsData] = useState({
    constructionNotes: '',
    labourCost: 0,
    materialCost: 0
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Spaces editing
  const [editingSpaces, setEditingSpaces] = useState(false);
  const [spacesData, setSpacesData] = useState<string[]>([]);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

  // Color Collections management
  const [colorCollections, setColorCollections] = useState<any[]>([]);
  const [loadingColorCollections, setLoadingColorCollections] = useState(false);
  const [showAddColorCollectionModal, setShowAddColorCollectionModal] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [uploadingCollectionImage, setUploadingCollectionImage] = useState<string | null>(null);
  const [collectionImages, setCollectionImages] = useState<{ [key: string]: string }>({});
  const collectionImageInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [confirmDeleteCollection, setConfirmDeleteCollection] = useState<{ show: boolean; collectionId: string | null; collectionName: string | null }>({ show: false, collectionId: null, collectionName: null });

  // Models management
  const [packageModels, setPackageModels] = useState<PackageModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [showAddModelsModal, setShowAddModelsModal] = useState(false);
  const [showEditModelsModal, setShowEditModelsModal] = useState(false);
  const [editingModel, setEditingModel] = useState<PackageModel | null>(null);

  // Units management
  const [packageUnits, setPackageUnits] = useState<PackageUnit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [showAddUnitsModal, setShowAddUnitsModal] = useState(false);
  const [showEditUnitsModal, setShowEditUnitsModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState<PackageUnit | null>(null);

  // Parent Package, Incompatible Packages, and Incompatible Options management
  const [showParentPackageModal, setShowParentPackageModal] = useState(false);
  const [showIncompatiblePackagesModal, setShowIncompatiblePackagesModal] = useState(false);
  const [showIncompatibleOptionsModal, setShowIncompatibleOptionsModal] = useState(false);
  const [showIncludedOptionsModal, setShowIncludedOptionsModal] = useState(false);
  const [selectedIncludedOptions, setSelectedIncludedOptions] = useState<string[]>([]);
  const [addingIncludedOptions, setAddingIncludedOptions] = useState(false);
  const [deletingIncludedOptionId, setDeletingIncludedOptionId] = useState<string | null>(null);
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  const [availableOptions, setAvailableOptions] = useState<any[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [confirmDeleteParentPackage, setConfirmDeleteParentPackage] = useState<{ show: boolean; packageName: string | null }>({ show: false, packageName: null });
  const [confirmDeleteIncompatiblePackage, setConfirmDeleteIncompatiblePackage] = useState<{ show: boolean; packageId: string | null; packageName: string | null }>({ show: false, packageId: null, packageName: null });

  // Purchased Units
  const [purchasedUnits, setPurchasedUnits] = useState<PurchasedUnit[]>([]);
  const [loadingPurchasedUnits, setLoadingPurchasedUnits] = useState(false);
  const [purchasedUnitsPagination, setPurchasedUnitsPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Tabs
  const [activeTab, setActiveTab] = useState<'general' | 'settings' | 'models' | 'units' | 'purchased'>('general');

  // Change history drawer
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    fetchProjects();
    fetchPackage();
    // Clear stale data when package changes
    setPackageModels([]);
    setPackageUnits([]);
  }, [packageId]);

  useEffect(() => {
    if (pkg?.projectId) {
      fetchLocations(pkg.projectId);
    }
  }, [pkg?.projectId]);

  useEffect(() => {
    if (activeTab === 'settings' && pkg?.projectId) {
      fetchColorCollections();
    }
  }, [activeTab, pkg?.projectId]);

  useEffect(() => {
    if (activeTab === 'models') {
      fetchPackageModels();
    }
  }, [activeTab, packageId]);

  useEffect(() => {
    if (activeTab === 'units') {
      fetchPackageUnits();
    }
  }, [activeTab, packageId]);

  useEffect(() => {
    if (activeTab === 'purchased') {
      fetchPurchasedUnits();
    }
  }, [activeTab, purchasedUnitsPagination.page, purchasedUnitsPagination.limit]);

  const fetchProjects = async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: { limit: 1000, sortBy: 'name', sortOrder: 'asc' }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchLocations = async (projectId: string) => {
    if (!projectId) {
      setLocations([]);
      return;
    }

    try {
      setLoadingLocations(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkglocations`, {
        params: {
          projectId: projectId,
          limit: 1000,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });
      setLocations(response.data.locations || []);
    } catch (err: any) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchPackage = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
      );
      const data = response.data.package;
      setPkg(data);
      setGeneralInfoData({
        name: data.name || '',
        caption: data.caption || '',
        description: data.description || '',
        location: data.location || '',
        phase: data.phase || '',
        isActive: data.isActive ?? true,
        order: data.order || 0,
        materialType: data.materialType || '',
        size: data.size || '',
        modelCategoryCode: data.modelCategoryCode || '',
        finish: data.finish || '',
        selectionColor: data.selectionColor || '',
        associatedTrades: data.associatedTrades || '',
        includedItemModels: data.includedItemModels || '',
        specifications: data.specifications || ''
      });
      setDescriptionData({
        description: data.description || '',
        constructionNotes: data.constructionNotes || ''
      });
      setSettingsData({
        constructionNotes: data.constructionNotes || '',
        labourCost: data.labourCost || 0,
        materialCost: data.materialCost || 0
      });

      // Fetch hero image if exists
      if (data.heroImageId) {
        fetchHeroImage(data.heroImageId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch package');
      console.error('Error fetching package:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeroImage = async (imageId: string) => {
    try {
      const images = await getImages(workspaceId, {
        entityType: 'package',
        entityId: packageId,
        imageType: 'hero'
      });
      if (images.length > 0) {
        setHeroImageUrl(images[0].url);
      }
    } catch (err) {
      console.error('Error fetching hero image:', err);
    }
  };

  const handleGeneralInfoChange = (field: string, value: any) => {
    setGeneralInfoData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveGeneralInfo = async () => {
    try {
      setSavingGeneralInfo(true);

      // Save general info (excluding description and constructionNotes which have their own section)
      const { description, ...generalInfoWithoutDescription } = generalInfoData;

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        generalInfoWithoutDescription,
      );

      // Update local state directly instead of fetching
      setPkg(prev => prev ? { ...prev, ...generalInfoWithoutDescription } : null);
      setEditingGeneralInfo(false);
      if (onSave) onSave();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save package');
      console.error('Error saving package:', err);
    } finally {
      setSavingGeneralInfo(false);
    }
  };

  const handleDescriptionChange = (field: string, value: any) => {
    setDescriptionData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDescription = async () => {
    try {
      setSavingDescription(true);

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        descriptionData,
      );

      // Update local state directly instead of fetching
      setPkg(prev => prev ? { ...prev, ...descriptionData } : null);
      setEditingDescription(false);
      if (onSave) onSave();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save description');
      console.error('Error saving description:', err);
    } finally {
      setSavingDescription(false);
    }
  };

  const handleSettingsChange = (field: string, value: any) => {
    setSettingsData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);

      // Only update cost fields
      const updateData = {
        labourCost: settingsData.labourCost,
        materialCost: settingsData.materialCost
      };

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        updateData,
      );

      // Update local state directly instead of fetching
      setPkg(prev => prev ? { ...prev, ...updateData } : null);
      setEditingSettings(false);
      if (onSave) onSave();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save settings');
      console.error('Error saving settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // Spaces handlers
  const handleEditSpaces = () => {
    if (!pkg) return;
    setSpacesData(pkg.spaces || []);
    setEditingSpaces(true);
  };

  const handleCancelSpaces = () => {
    setEditingSpaces(false);
  };

  const handleSpacesChange = (spaces: string[]) => {
    setSpacesData(spaces);
  };

  const handleAddSpacesFromModal = (newSpaces: string[]) => {
    setSpacesData(prev => [...prev, ...newSpaces]);
  };

  const handleSaveSpaces = async () => {
    if (!pkg) return;

    try {
      setSavingSpaces(true);

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { spaces: spacesData },
      );

      // Update local state directly instead of fetching
      setPkg(prev => prev ? { ...prev, spaces: spacesData } : null);
      setEditingSpaces(false);
      if (onSave) onSave();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save spaces');
      console.error('Error saving spaces:', err);
    } finally {
      setSavingSpaces(false);
    }
  };

  // Color Collections handlers
  const fetchColorCollections = async () => {
    if (!pkg?.projectId) return;

    try {
      setLoadingColorCollections(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-color-collections`, {
        params: { projectId: pkg.projectId }
      });
      setColorCollections(response.data.colorCollections || []);

      // Fetch images for all collections
      await fetchCollectionImages();
    } catch (err: any) {
      console.error('Error fetching color collections:', err);
    } finally {
      setLoadingColorCollections(false);
    }
  };

  const fetchCollectionImages = async () => {
    if (!pkg?.collections) return;

    const imageMap: { [key: string]: string } = {};

    // Fetch images for each collection
    for (const collection of pkg.collections) {
      try {
        const images = await getImages(workspaceId, {
          entityType: 'package',
          entityId: `${packageId}_collection_${collection.collectionId}`,
          imageType: 'thumbnail'
        });

        if (images.length > 0) {
          imageMap[collection.collectionId] = images[0].url;
        }
      } catch (err) {
        console.error(`Error fetching image for collection ${collection.collectionId}:`, err);
      }
    }

    setCollectionImages(imageMap);
  };

  const handleToggleCollection = (collectionId: string) => {
    setSelectedCollections(prev =>
      prev.includes(collectionId)
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const handleAddSelectedCollections = async () => {
    if (selectedCollections.length === 0) {
      toast.warning('Please select at least one color collection');
      return;
    }

    try {
      const collectionsToAdd = selectedCollections.map(collectionId => {
        const collection = colorCollections.find(c => c._id === collectionId);
        return {
          collectionId: collection._id,
          name: collection.name
        };
      });

      const updatedCollections = [
        ...(pkg?.collections || []),
        ...collectionsToAdd
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { collections: updatedCollections },
      );

      // Update local state directly instead of fetching
      setPkg(prev => prev ? { ...prev, collections: updatedCollections } : null);
      setSelectedCollections([]);
      setShowAddColorCollectionModal(false);
      toast.success(`${selectedCollections.length} color collection(s) added successfully`);
    } catch (err: any) {
      console.error('Error adding color collections:', err);
      toast.error(err.response?.data?.message || 'Failed to add color collections');
    }
  };

  const handleRemoveColorCollection = (collectionId: string, collectionName: string) => {
    setConfirmDeleteCollection({ show: true, collectionId, collectionName });
  };

  const confirmRemoveColorCollection = async () => {
    if (!confirmDeleteCollection.collectionId) return;

    try {
      const updatedCollections = (pkg?.collections || []).filter(
        c => c.collectionId !== confirmDeleteCollection.collectionId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { collections: updatedCollections },
      );

      // Update local state directly instead of fetching
      setPkg(prev => prev ? { ...prev, collections: updatedCollections } : null);
      setConfirmDeleteCollection({ show: false, collectionId: null, collectionName: null });
    } catch (err: any) {
      console.error('Error removing color collection:', err);
      toast.error(err.response?.data?.message || 'Failed to remove color collection');
    }
  };

  const handleCollectionImageClick = (collectionId: string) => {
    collectionImageInputRefs.current[collectionId]?.click();
  };

  const handleCollectionImageUpload = async (collectionId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    try {
      setUploadingCollectionImage(collectionId);

      // Upload image with package entity type
      // entityId combines packageId and collectionId to uniquely identify this package's collection image
      await uploadImage({
        workspaceId,
        file,
        entityType: 'package',
        entityId: `${packageId}_collection_${collectionId}`,
        imageType: 'thumbnail',
        title: `Color Collection - ${pkg?.collections?.find(c => c.collectionId === collectionId)?.name || collectionId}`
      });

      // Refresh images to show the uploaded image
      await fetchCollectionImages();
      toast.success('Image uploaded successfully');

    } catch (err: any) {
      console.error('Error uploading collection image:', err);
      toast.error(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingCollectionImage(null);
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    }
  };

  // Models handlers
  const fetchPackageModels = async () => {
    try {
      setLoadingModels(true);
      const response = await packageModelService.getByPackage(workspaceId, packageId);
      setPackageModels(response.models || []);
    } catch (err: any) {
      console.error('Error fetching package models:', err);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleAddModels = async (models: Array<{ modelId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      await packageModelService.createMultiple(workspaceId, packageId, models);
      await fetchPackageModels();
      setShowAddModelsModal(false);
    } catch (err: any) {
      console.error('Error adding models:', err);
      toast.error(err.response?.data?.message || 'Failed to add models');
    }
  };

  const handleEditModel = (model: PackageModel) => {
    setEditingModel(model);
    setShowEditModelsModal(true);
  };

  const handleUpdateModel = async (modelId: string, data: { isUpgrade?: boolean; cost?: number; price?: number }) => {
    try {
      await packageModelService.update(workspaceId, packageId, modelId, data);
      await fetchPackageModels();
      setShowEditModelsModal(false);
      setEditingModel(null);
    } catch (err: any) {
      console.error('Error updating model:', err);
      toast.error(err.response?.data?.message || 'Failed to update model');
    }
  };

  const handleEditModels = async (models: Array<{ modelId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingModels(true);
      // Update all models in a single API call
      await packageModelService.updateMultiple(
        workspaceId,
        packageId,
        models
      );
      await fetchPackageModels();
      setShowEditModelsModal(false);
    } catch (err: any) {
      console.error('Error updating models:', err);
      toast.error(err.response?.data?.message || 'Failed to update models');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleDeleteModel = (modelId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Model',
      message: 'Are you sure you want to remove this model?',
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await packageModelService.delete(workspaceId, packageId, modelId);
          // Update local state directly
          setPackageModels(prev => prev.filter(m => m.modelId !== modelId));
        } catch (err: any) {
          console.error('Error deleting model:', err);
          toast.error(err.response?.data?.message || 'Failed to delete model');
        }
      }
    });
  };

  // Units handlers
  const fetchPackageUnits = async () => {
    try {
      setLoadingUnits(true);
      const response = await packageUnitService.getByPackage(workspaceId, packageId);
      setPackageUnits(response.units || []);
    } catch (err: any) {
      console.error('Error fetching package units:', err);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleAddUnits = async (units: Array<{ unitId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      await packageUnitService.createMultiple(workspaceId, packageId, units);
      await fetchPackageUnits();
      setShowAddUnitsModal(false);
    } catch (err: any) {
      console.error('Error adding units:', err);
      toast.error(err.response?.data?.message || 'Failed to add units');
    }
  };

  const handleEditUnit = (unit: PackageUnit) => {
    setEditingUnit(unit);
    setShowEditUnitsModal(true);
  };

  const handleUpdateUnit = async (unitId: string, data: { isUpgrade?: boolean; cost?: number; price?: number }) => {
    try {
      await packageUnitService.update(workspaceId, packageId, unitId, data);
      await fetchPackageUnits();
      setShowEditUnitsModal(false);
      setEditingUnit(null);
    } catch (err: any) {
      console.error('Error updating unit:', err);
      toast.error(err.response?.data?.message || 'Failed to update unit');
    }
  };

  const handleEditUnits = async (units: Array<{ unitId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingUnits(true);
      // Update all units in a single API call
      await packageUnitService.updateMultiple(
        workspaceId,
        packageId,
        units
      );
      await fetchPackageUnits();
      setShowEditUnitsModal(false);
    } catch (err: any) {
      console.error('Error updating units:', err);
      toast.error(err.response?.data?.message || 'Failed to update units');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleDeleteUnit = (unitId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Unit',
      message: 'Are you sure you want to remove this unit?',
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await packageUnitService.delete(workspaceId, packageId, unitId);
          // Update local state directly
          setPackageUnits(prev => prev.filter(u => u.unitId !== unitId));
        } catch (err: any) {
          console.error('Error deleting unit:', err);
          toast.error(err.response?.data?.message || 'Failed to delete unit');
        }
      }
    });
  };

  // Purchased Units handlers
  const fetchPurchasedUnits = async () => {
    try {
      setLoadingPurchasedUnits(true);

      // Fetch purchased units from the API endpoint
      const response = await api.get(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}/purchased-units`,
        {
          params: {
            page: purchasedUnitsPagination.page,
            limit: purchasedUnitsPagination.limit
          }
        }
      );

      setPurchasedUnits(response.data.purchasedUnits || []);
      if (response.data.pagination) {
        setPurchasedUnitsPagination(response.data.pagination);
      }
    } catch (err: any) {
      console.error('Error fetching purchased units:', err);
      setError(err.response?.data?.message || 'Failed to fetch purchased units');
    } finally {
      setLoadingPurchasedUnits(false);
    }
  };

  const handlePurchasedUnitsPageChange = (page: number) => {
    setPurchasedUnitsPagination({ ...purchasedUnitsPagination, page });
  };

  const handlePurchasedUnitsLimitChange = (limit: number) => {
    setPurchasedUnitsPagination({ ...purchasedUnitsPagination, limit, page: 1 });
  };

  // Parent Package / Incompatible Items handlers
  const fetchAvailablePackages = async () => {
    if (!pkg?.projectId) return;

    try {
      setLoadingPackages(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-packages`, {
        params: { projectId: pkg.projectId, limit: 1000, sortBy: 'name', sortOrder: 'asc' }
      });
      // Filter out the current package
      setAvailablePackages(response.data.packages.filter((p: any) => p._id !== packageId));
    } catch (err: any) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoadingPackages(false);
    }
  };

  const fetchAvailableOptions = async () => {
    if (!pkg?.projectId) return;

    try {
      setLoadingOptions(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-options`, {
        params: { projectId: pkg.projectId, limit: 1000, sortBy: 'name', sortOrder: 'asc' }
      });
      setAvailableOptions(response.data.options || []);
    } catch (err: any) {
      console.error('Error fetching options:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleSetParentPackage = async (parentPkgId: string) => {
    try {
      const selectedPackage = availablePackages.find(p => p._id === parentPkgId);

      if (!selectedPackage) return;

      const parentPackage = {
        id: selectedPackage._id,
        name: selectedPackage.name
      };

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { parentPackage },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, parentPackage } : null);
      setShowParentPackageModal(false);
    } catch (err: any) {
      console.error('Error setting parent package:', err);
      toast.error(err.response?.data?.message || 'Failed to set parent package');
    }
  };

  const handleRemoveParentPackage = (packageName: string) => {
    setConfirmDeleteParentPackage({ show: true, packageName });
  };

  const confirmRemoveParentPackage = async () => {
    try {
      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { parentPackage: null },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, parentPackage: undefined } : null);
      setConfirmDeleteParentPackage({ show: false, packageName: null });
    } catch (err: any) {
      console.error('Error removing parent package:', err);
      toast.error(err.response?.data?.message || 'Failed to remove parent package');
    }
  };

  const handleAddIncompatiblePackage = async (incompatiblePkgId: string) => {
    try {
      const selectedPackage = availablePackages.find(p => p._id === incompatiblePkgId);

      if (!selectedPackage) return;

      const updatedIncompatiblePackages = [
        ...(pkg?.incompatiblePackages || []),
        {
          _id: selectedPackage._id,
          name: selectedPackage.name
        }
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { incompatiblePackages: updatedIncompatiblePackages },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, incompatiblePackages: updatedIncompatiblePackages } : null);
      setShowIncompatiblePackagesModal(false);
    } catch (err: any) {
      console.error('Error adding incompatible package:', err);
      toast.error(err.response?.data?.message || 'Failed to add incompatible package');
    }
  };

  const handleRemoveIncompatiblePackage = (incompatiblePkgId: string, packageName: string) => {
    setConfirmDeleteIncompatiblePackage({ show: true, packageId: incompatiblePkgId, packageName });
  };

  const confirmRemoveIncompatiblePackage = async () => {
    if (!confirmDeleteIncompatiblePackage.packageId) return;

    try {
      const updatedIncompatiblePackages = (pkg?.incompatiblePackages || []).filter(
        p => p._id !== confirmDeleteIncompatiblePackage.packageId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { incompatiblePackages: updatedIncompatiblePackages },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, incompatiblePackages: updatedIncompatiblePackages } : null);
      setConfirmDeleteIncompatiblePackage({ show: false, packageId: null, packageName: null });
    } catch (err: any) {
      console.error('Error removing incompatible package:', err);
      toast.error(err.response?.data?.message || 'Failed to remove incompatible package');
    }
  };

  const handleAddIncompatibleOption = async (optionId: string) => {
    try {
      const selectedOption = availableOptions.find(o => o._id === optionId);

      if (!selectedOption) return;

      const updatedIncompatibleOptions = [
        ...(pkg?.incompatiblePersonalizationOptions || []),
        {
          _id: selectedOption._id,
          name: selectedOption.name
        }
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { incompatiblePersonalizationOptions: updatedIncompatibleOptions },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, incompatiblePersonalizationOptions: updatedIncompatibleOptions } : null);
      setShowIncompatibleOptionsModal(false);
    } catch (err: any) {
      console.error('Error adding incompatible option:', err);
      toast.error(err.response?.data?.message || 'Failed to add incompatible option');
    }
  };

  const handleRemoveIncompatibleOption = async (optionId: string) => {
    try {
      const updatedIncompatibleOptions = (pkg?.incompatiblePersonalizationOptions || []).filter(
        o => o._id !== optionId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { incompatiblePersonalizationOptions: updatedIncompatibleOptions },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, incompatiblePersonalizationOptions: updatedIncompatibleOptions } : null);
    } catch (err: any) {
      console.error('Error removing incompatible option:', err);
      toast.error(err.response?.data?.message || 'Failed to remove incompatible option');
    }
  };

  // Included Options handlers
  const handleAddIncludedOptions = async () => {
    if (selectedIncludedOptions.length === 0) {
      toast.warning('Please select at least one option');
      return;
    }

    try {
      setAddingIncludedOptions(true);
      const newOptions = selectedIncludedOptions.map(optionId => {
        const option = availableOptions.find(o => o._id === optionId);
        return {
          _id: option._id,
          name: option.name
        };
      });

      const updatedIncludedOptions = [
        ...(pkg?.includedOptions || []),
        ...newOptions
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { includedOptions: updatedIncludedOptions },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, includedOptions: updatedIncludedOptions } : null);
      setShowIncludedOptionsModal(false);
      setSelectedIncludedOptions([]);
      toast.success(`${selectedIncludedOptions.length} option(s) added successfully`);
    } catch (err: any) {
      console.error('Error adding included options:', err);
      toast.error(err.response?.data?.message || 'Failed to add options');
    } finally {
      setAddingIncludedOptions(false);
    }
  };

  const toggleIncludedOptionSelection = (optionId: string) => {
    setSelectedIncludedOptions(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleRemoveIncludedOption = async (optionId: string) => {
    try {
      setDeletingIncludedOptionId(optionId);
      const updatedIncludedOptions = (pkg?.includedOptions || []).filter(
        o => o._id !== optionId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { includedOptions: updatedIncludedOptions },
      );

      // Update local state directly
      setPkg(prev => prev ? { ...prev, includedOptions: updatedIncludedOptions } : null);
      toast.success('Option removed successfully');
    } catch (err: any) {
      console.error('Error removing included option:', err);
      toast.error(err.response?.data?.message || 'Failed to remove option');
    } finally {
      setDeletingIncludedOptionId(null);
    }
  };

  const handleHeroImageClick = () => {
    heroInputRef.current?.click();
  };

  const handleHeroImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    try {
      setUploadingHero(true);
      const uploaded = await uploadImage({
        workspaceId,
        file,
        entityType: 'package',
        entityId: packageId,
        imageType: 'hero'
      });
      setHeroImageUrl(uploaded.url);

      // Update the package with heroImageId
      await api.put(
        `/workspaces/${workspaceId}/pkg-packages/${packageId}`,
        { heroImageId: uploaded._id },
      );
    } catch (err) {
      console.error('Error uploading hero image:', err);
    } finally {
      setUploadingHero(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Prevent body from scrolling while the slider is open to avoid double scrollbars
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (loading) {
    return (
      <div className="package-details-slider">
        <div className="package-details-header">
          <div className="package-details-title">
            <h2>Loading...</h2>
          </div>
          <button className="package-details-close" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>
        <div className="package-details-content">
          <ShimmerLoader type="details" />
        </div>
      </div>
    );
  }

  const phases = [
    { name: 'One' },
    { name: 'Two' },
    { name: 'Three' },
    { name: 'Four' },
    { name: 'Five' }
  ];

  return (
    <div className="package-details-slider">
      {/* Header */}
      <div className="package-details-header">
        <div className="package-details-header-left">
          <button className="package-details-back-btn" onClick={handleClose}>
            ← Back
          </button>
          <div className="package-details-title">
            <h2>{pkg?.name || 'Package'}</h2>
            <p>{pkg?.projectName || 'Package Details'}</p>
          </div>
        </div>
        <button className="package-details-close" onClick={handleClose}>
          <X size={24} />
        </button>
      </div>

      {/* Tabs */}
      <div className="package-details-tabs">
          <button
            className={`package-details-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button
            className={`package-details-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button
            className={`package-details-tab ${activeTab === 'models' ? 'active' : ''}`}
            onClick={() => setActiveTab('models')}
          >
            Models
          </button>
          <button
            className={`package-details-tab ${activeTab === 'units' ? 'active' : ''}`}
            onClick={() => setActiveTab('units')}
          >
            Units
          </button>
          <button
            className={`package-details-tab ${activeTab === 'purchased' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchased')}
          >
            Purchased Units
          </button>
      </div>

  {/* Content */}
      <div className="package-details-content">
        {/* General Tab */}
        {activeTab === 'general' && (
          <>
                {/* First Row: Hero Image + Basic Information */}
                <div className="package-details-row">
                  {/* Hero Photo Tile - Left Side */}
                  <div className="package-details-tile" style={{ flex: '0 0 33.33%' }}>
                    <div className="package-details-tile-header">
                      <h3>Hero Image</h3>
                      <button
                        className="package-details-edit-btn"
                        onClick={handleHeroImageClick}
                        style={{ marginLeft: 'auto' }}
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                    <div className="package-details-tile-content">
                      <div
                        onClick={handleHeroImageClick}
                        style={{
                          border: '2px dashed #D1D5DB',
                          borderRadius: '8px',
                          padding: '32px',
                          textAlign: 'center',
                          backgroundColor: '#F9FAFB',
                          cursor: 'pointer',
                          minHeight: '300px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {uploadingHero ? (
                          <div className="builders-loading">
                            <div className="builders-loading-spinner" style={{ width: '48px', height: '48px' }} />
                            <p style={{ marginTop: '16px', fontSize: '14px', color: '#6B7280' }}>Uploading...</p>
                          </div>
                        ) : heroImageUrl ? (
                          <img
                            src={heroImageUrl}
                            alt="Hero"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          <>
                            <PackageIcon size={48} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
                            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                              Drag & drop image here
                            </p>
                            <p style={{ fontSize: '12px', color: '#6B7280' }}>
                              or click to browse
                            </p>
                          </>
                        )}
                      </div>
                      <input
                        ref={heroInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleHeroImageChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>

                  {/* Basic Information Tile */}
                  <div className="package-details-tile" style={{ flex: '1' }}>
                    <div className="package-details-tile-header">
                      <h3>Basic Information</h3>
                      {!editingGeneralInfo && (
                        <button
                          className="package-details-edit-btn"
                          onClick={() => setEditingGeneralInfo(true)}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                    <div className="package-details-tile-content">
                      {editingGeneralInfo ? (
                        <div className="package-details-form">
                          {/* Name and Order */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '12px' }}>
                            <div className="form-group">
                              <label>Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.name}
                                onChange={(e) => handleGeneralInfoChange('name', e.target.value)}
                                placeholder="Enter package name"
                              />
                            </div>
                            <div className="form-group">
                              <label>Order</label>
                              <input
                                type="number"
                                className="form-input"
                                value={generalInfoData.order}
                                disabled
                                style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
                              />
                            </div>
                          </div>

                          {/* Active, Valid with another package */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'end' }}>
                            <label className="drawer-form-checkbox-label" style={{ paddingBottom: '10px' }}>
                              <input
                                type="checkbox"
                                checked={generalInfoData.isActive}
                                onChange={(e) => handleGeneralInfoChange('isActive', e.target.checked)}
                              />
                              <span>Active</span>
                            </label>
                            <label className="drawer-form-checkbox-label" style={{ paddingBottom: '10px' }}>
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={() => {}}
                              />
                              <span>Valid with another package</span>
                            </label>
                          </div>

                          {/* Locations and Phase */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label>Locations *</label>
                              {loadingLocations ? (
                                <select className="form-input" disabled>
                                  <option>Loading locations...</option>
                                </select>
                              ) : (
                                <select
                                  className="form-input"
                                  value={generalInfoData.location}
                                  onChange={(e) => handleGeneralInfoChange('location', e.target.value)}
                                >
                                  <option value="">Select Location</option>
                                  {/* Show current value if it's not in the fetched locations */}
                                  {generalInfoData.location && !locations.find(loc => loc.name === generalInfoData.location) && (
                                    <option value={generalInfoData.location}>{generalInfoData.location}</option>
                                  )}
                                  {locations.map(loc => (
                                    <option key={loc._id} value={loc.name}>{loc.name}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <div className="form-group">
                              <label>Phase *</label>
                              <select
                                className="form-input"
                                value={generalInfoData.phase}
                                onChange={(e) => handleGeneralInfoChange('phase', e.target.value)}
                              >
                                <option value="">Select Phase</option>
                                {phases.map(phase => (
                                  <option key={phase.name} value={phase.name}>{phase.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Material/Type and Size */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label>Material / Type:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.materialType}
                                onChange={(e) => handleGeneralInfoChange('materialType', e.target.value)}
                                placeholder="Enter material or type"
                              />
                            </div>
                            <div className="form-group">
                              <label>Size:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.size}
                                onChange={(e) => handleGeneralInfoChange('size', e.target.value)}
                                placeholder="Enter size"
                              />
                            </div>
                          </div>

                          {/* Model/Category Number/Code and Finish */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label>Model / Category Number / Code:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.modelCategoryCode}
                                onChange={(e) => handleGeneralInfoChange('modelCategoryCode', e.target.value)}
                                placeholder="Enter model/category number/code"
                              />
                            </div>
                            <div className="form-group">
                              <label>Finish:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.finish}
                                onChange={(e) => handleGeneralInfoChange('finish', e.target.value)}
                                placeholder="Enter finish"
                              />
                            </div>
                          </div>

                          {/* Selection/Color and Caption */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label>Selection / Color:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.selectionColor}
                                onChange={(e) => handleGeneralInfoChange('selectionColor', e.target.value)}
                                placeholder="Enter selection or color"
                              />
                            </div>
                            <div className="form-group">
                              <label>Caption:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.caption}
                                onChange={(e) => handleGeneralInfoChange('caption', e.target.value)}
                                placeholder="Enter caption"
                              />
                            </div>
                          </div>

                          {/* Associated Trades and Included Item Models */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label>Associated Trades :</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.associatedTrades}
                                onChange={(e) => handleGeneralInfoChange('associatedTrades', e.target.value)}
                                placeholder="Enter associated trades"
                              />
                            </div>
                            <div className="form-group">
                              <label>Included Item Models:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.includedItemModels}
                                onChange={(e) => handleGeneralInfoChange('includedItemModels', e.target.value)}
                                placeholder="Enter included item models"
                              />
                            </div>
                          </div>

                          {/* Specifications */}
                          <div className="form-row" style={{ display: 'block' }}>
                            <div className="form-group" style={{ width: '100%' }}>
                              <label>Specifications:</label>
                              <textarea
                                className="form-input"
                                value={generalInfoData.specifications}
                                onChange={(e) => handleGeneralInfoChange('specifications', e.target.value)}
                                placeholder="Type something"
                                rows={5}
                                style={{ width: '100%', resize: 'vertical' }}
                              />
                            </div>
                          </div>

                          <div className="package-details-form-actions">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setEditingGeneralInfo(false);
                                if (pkg) {
                                  setGeneralInfoData({
                                    name: pkg.name || '',
                                    caption: pkg.caption || '',
                                    description: pkg.description || '',
                                    location: pkg.location || '',
                                    phase: pkg.phase || '',
                                    isActive: pkg.isActive ?? true,
                                    order: pkg.order || 0,
                                    materialType: pkg.materialType || '',
                                    size: pkg.size || '',
                                    modelCategoryCode: pkg.modelCategoryCode || '',
                                    finish: pkg.finish || '',
                                    selectionColor: pkg.selectionColor || '',
                                    associatedTrades: pkg.associatedTrades || '',
                                    includedItemModels: pkg.includedItemModels || '',
                                    specifications: pkg.specifications || ''
                                  });
                                }
                              }}
                              disabled={savingGeneralInfo}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveGeneralInfo}
                              disabled={savingGeneralInfo || !generalInfoData.name}
                            >
                              {savingGeneralInfo ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="package-details-info">
                          {/* Row 1: Name and Caption */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Name</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {pkg?.name || '-'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Caption</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {pkg?.caption || ''}</span>
                            </div>
                          </div>

                          {/* Row 2: Location and Phase */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Location</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {pkg?.location || '-'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Phase</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {pkg?.phase || '-'}</span>
                            </div>
                          </div>

                          {/* Row 3: Active and Order */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Active</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {pkg?.isActive ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Order</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {pkg?.order}</span>
                            </div>
                          </div>

                          {/* Row 4: Only valid with another package (full width) */}
                          <div className="info-row" style={{ display: 'flex', marginBottom: '8px' }}>
                            <span className="info-label" style={{ minWidth: '220px', fontWeight: 500, color: '#6B7280' }}>Only valid with another package</span>
                            <span className="info-value" style={{ color: '#111827' }}>: No</span>
                          </div>

                          {/* Row 5: Specifications and Included Item Models */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '140px', fontWeight: 500, color: '#6B7280' }}>Specifications</span>
                              <span className="info-value" style={{ color: '#111827' }}>: </span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '160px', fontWeight: 500, color: '#6B7280' }}>Included Item Models</span>
                              <span className="info-value" style={{ color: '#111827' }}>: </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>

                {/* Second Row: Description & Construction Notes + Spaces & Labour Cost */}
                <div className="package-details-row">
                  {/* Description & Construction Notes - Left 50% */}
                  <div className="package-details-tile" style={{ flex: '1' }}>
                    <div className="package-details-tile-header">
                      <h3>Description & Construction Notes</h3>
                      {!editingDescription && (
                        <button
                          className="package-details-edit-btn"
                          onClick={() => setEditingDescription(true)}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                    <div className="package-details-tile-content">
                      {editingDescription ? (
                        <div className="package-details-form">
                          <div className="form-row" style={{ display: 'block' }}>
                            <div className="form-group" style={{ width: '100%' }}>
                              <label>Description</label>
                              <RichTextEditor
                                content={descriptionData.description}
                                onChange={(content) => handleDescriptionChange('description', content)}
                                placeholder="Enter description"
                              />
                            </div>
                          </div>

                          <div className="form-row" style={{ display: 'block' }}>
                            <div className="form-group" style={{ width: '100%' }}>
                              <label>Construction Notes</label>
                              <RichTextEditor
                                content={descriptionData.constructionNotes}
                                onChange={(content) => handleDescriptionChange('constructionNotes', content)}
                                placeholder="Enter construction notes"
                              />
                            </div>
                          </div>

                          <div className="package-details-form-actions">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setEditingDescription(false);
                                if (pkg) {
                                  setDescriptionData({
                                    description: pkg.description || '',
                                    constructionNotes: pkg.constructionNotes || ''
                                  });
                                }
                              }}
                              disabled={savingDescription}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveDescription}
                              disabled={savingDescription}
                            >
                              {savingDescription ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="package-details-info">
                          <div style={{ marginBottom: '20px' }}>
                            <div className="info-row" style={{ marginBottom: '8px' }}>
                              <span className="info-label" style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Description</span>
                            </div>
                            <div className="info-row">
                              <div
                                className="info-value"
                                style={{ marginLeft: 0, color: '#6B7280', lineHeight: '1.6' }}
                                dangerouslySetInnerHTML={{ __html: pkg?.description || 'No description provided' }}
                              />
                            </div>
                          </div>
                          <div>
                            <div className="info-row" style={{ marginBottom: '8px' }}>
                              <span className="info-label" style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Construction Notes</span>
                            </div>
                            <div className="info-row">
                              <div
                                className="info-value"
                                style={{ marginLeft: 0, color: '#6B7280', lineHeight: '1.6' }}
                                dangerouslySetInnerHTML={{ __html: pkg?.constructionNotes || 'No construction notes provided' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side - Spaces & Labour Cost stacked vertically - 50% */}
                  <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '0px' }}>
                    {/* Spaces Tile */}
                    <div className="package-details-tile" style={{ flex: '1' }}>
                      <div className="package-details-tile-header">
                        <h3>Spaces</h3>
                        {!editingSpaces && (
                          <button
                            className="package-details-edit-btn"
                            onClick={handleEditSpaces}
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                      </div>
                      <div className="package-details-tile-content">
                        {editingSpaces ? (
                          <>
                            <div style={{ marginBottom: '16px' }}>
                              <SortableList
                                items={spacesData}
                                onChange={handleSpacesChange}
                                placeholder="Add custom space name"
                                addButtonText="+"
                              />
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowAddSpacesModal(true)}
                                style={{ marginTop: '12px' }}
                              >
                                + Add from List
                              </button>
                            </div>
                            <div className="package-details-form-actions">
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleCancelSpaces}
                                disabled={savingSpaces}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSaveSpaces}
                                disabled={savingSpaces}
                              >
                                {savingSpaces ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="package-details-info">
                            {pkg?.spaces && pkg.spaces.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {pkg.spaces.map((space, index) => (
                                  <span
                                    key={index}
                                    style={{
                                      padding: '4px 12px',
                                      backgroundColor: '#F3F4F6',
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      color: '#374151'
                                    }}
                                  >
                                    {space}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p style={{ color: '#6B7280', textAlign: 'center', padding: '20px' }}>
                                No spaces added yet
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Labour & Material Cost */}
                    <div className="package-details-tile">
                    <div className="package-details-tile-header">
                      {!editingSettings && (
                        <button
                          className="package-details-edit-btn"
                          onClick={() => setEditingSettings(true)}
                          style={{ marginLeft: 'auto' }}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                    <div className="package-details-tile-content">
                      {editingSettings ? (
                        <div className="package-details-form">
                          <div className="form-row">
                            <div className="form-group" style={{ flex: '1' }}>
                              <label>Labour Cost</label>
                              <input
                                type="number"
                                className="form-input"
                                value={settingsData.labourCost}
                                onChange={(e) => handleSettingsChange('labourCost', parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group" style={{ flex: '1' }}>
                              <label>Material Cost</label>
                              <input
                                type="number"
                                className="form-input"
                                value={settingsData.materialCost}
                                onChange={(e) => handleSettingsChange('materialCost', parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </div>
                          </div>

                          <div className="package-details-form-actions">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setEditingSettings(false);
                                if (pkg) {
                                  setSettingsData({
                                    constructionNotes: pkg.constructionNotes || '',
                                    labourCost: pkg.labourCost || 0,
                                    materialCost: pkg.materialCost || 0
                                  });
                                }
                              }}
                              disabled={savingSettings}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveSettings}
                              disabled={savingSettings}
                            >
                              {savingSettings ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="package-details-info">
                          <div className="info-row" style={{ display: 'flex', marginBottom: '12px' }}>
                            <span className="info-label" style={{ minWidth: '120px', fontWeight: 500, color: '#6B7280' }}>Labour Cost</span>
                            <span className="info-value" style={{ color: '#111827' }}>: {pkg?.labourCost ? ` $${pkg.labourCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</span>
                          </div>
                          <div className="info-row" style={{ display: 'flex', marginBottom: '12px' }}>
                            <span className="info-label" style={{ minWidth: '120px', fontWeight: 500, color: '#6B7280' }}>Material Cost</span>
                            <span className="info-value" style={{ color: '#111827' }}>: {pkg?.materialCost ? ` $${pkg.materialCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                </div>

                {/* Fourth Row: Photo Gallery */}
                <div className="package-details-row">
                  <div className="package-details-tile" style={{ flex: '1' }}>
                    <div className="package-details-tile-header">
                      <h3>Photo Gallery</h3>
                    </div>
                    <div className="package-details-tile-content">
                      <PhotoGallery
                        workspaceId={workspaceId}
                        entityType="package"
                        entityId={packageId}
                        category="photo_gallery"
                        allowMultiple={true}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <>
                {/* Color Collections Section */}
                <div className="package-details-tile">
                  <div className="package-details-tile-header">
                    <h3>Color Collections</h3>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAddColorCollectionModal(true)}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Collections
                    </button>
                  </div>
                  <div className="package-details-tile-content">
                    {loadingColorCollections ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        Loading color collections...
                      </div>
                    ) : pkg?.collections && pkg.collections.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Image</th>
                              <th>Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pkg.collections.map((collection) => (
                              <tr key={collection.collectionId}>
                                <td>
                                  {collectionImages[collection.collectionId] ? (
                                    <img
                                      src={collectionImages[collection.collectionId]}
                                      alt={collection.name}
                                      style={{
                                        width: '60px',
                                        height: '60px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        border: '1px solid #E5E7EB'
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#F3F4F6',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #E5E7EB',
                                        color: '#9CA3AF'
                                      }}
                                    >
                                      <PackageIcon size={24} />
                                    </div>
                                  )}
                                </td>
                                <td>{collection.name || collection.collectionId}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <button
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => handleCollectionImageClick(collection.collectionId)}
                                      disabled={uploadingCollectionImage === collection.collectionId}
                                      style={{
                                        padding: '4px 12px',
                                        fontSize: '13px'
                                      }}
                                      title="Upload image for this color collection"
                                    >
                                      {uploadingCollectionImage === collection.collectionId ? 'Uploading...' : (collectionImages[collection.collectionId] ? 'Change Image' : 'Add Image')}
                                    </button>
                                    <input
                                      ref={el => { collectionImageInputRefs.current[collection.collectionId] = el; }}
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleCollectionImageUpload(collection.collectionId, e)}
                                      style={{ display: 'none' }}
                                    />
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      onClick={() => handleRemoveColorCollection(collection.collectionId, collection.name || 'this collection')}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: '#DC2626',
                                        color: '#fff',
                                        padding: '4px 8px'
                                      }}
                                      title="Remove collection"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        No color collections added yet. Click "Add Collections" to get started.
                      </div>
                    )}
                  </div>
                </div>

                {/* Parent Packages Section */}
                <div className="package-details-tile" style={{ marginTop: '20px' }}>
                  <div className="package-details-tile-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <h3>Parent Packages</h3>
                    </div>
                    <button
                      className="package-details-edit-btn"
                      onClick={() => {
                        fetchAvailablePackages();
                        setShowParentPackageModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      <Pencil size={16} />
                    </button>
                  </div>
                  <div className="package-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: Homeowner must purchase at least one the parent package with in this list to buy this current package.)
                    </p>
                    {pkg?.parentPackage ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Package Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{pkg.parentPackage.name || pkg.parentPackage.id}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#DC2626',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    marginLeft: 'auto'
                                  }}
                                  onClick={() => handleRemoveParentPackage(pkg.parentPackage?.name || 'this package')}
                                  title="Remove parent package"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No parent package found
                      </div>
                    )}
                  </div>
                </div>

                {/* Included Options Section */}
                <div className="package-details-tile" style={{ marginTop: '20px' }}>
                  <div className="package-details-tile-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <h3>Included Personalization Options</h3>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchAvailableOptions();
                        setSelectedIncludedOptions([]);
                        setShowIncludedOptionsModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Option
                    </button>
                  </div>
                  <div className="package-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: Personalization Options listed here are included with this package and will be automatically available to homeowners who purchase it.)
                    </p>
                    {pkg?.includedOptions && pkg.includedOptions.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Option Name</th>
                              <th style={{ width: '150px' }}>Location</th>
                              <th style={{ textAlign: 'right', width: '100px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pkg.includedOptions.map((option) => (
                              <tr key={option._id}>
                                <td>{option.name || option._id}</td>
                                <td>
                                  <button
                                    className="btn btn-sm"
                                    style={{
                                      background: '#000',
                                      color: '#fff',
                                      padding: '4px 12px',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Update Location
                                  </button>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className={`btn btn-sm btn-ghost ${deletingIncludedOptionId === option._id ? 'btn-loading' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto',
                                      minWidth: '32px',
                                      justifyContent: 'center'
                                    }}
                                    onClick={() => handleRemoveIncludedOption(option._id)}
                                    disabled={deletingIncludedOptionId === option._id}
                                    title="Remove option"
                                  >
                                    {deletingIncludedOptionId === option._id ? '' : <Trash2 size={16} />}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No Included Options Found
                      </div>
                    )}
                  </div>
                </div>

                {/* Incompatible Packages Section */}
                <div className="package-details-tile" style={{ marginTop: '20px' }}>
                  <div className="package-details-tile-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <h3>Incompatible Packages</h3>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchAvailablePackages();
                        setShowIncompatiblePackagesModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Package
                    </button>
                  </div>
                  <div className="package-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      ( Help: If Homeowner purchases any one the packages listed here, this current package will not be available to user.)
                    </p>
                    {pkg?.incompatiblePackages && pkg.incompatiblePackages.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Package Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pkg.incompatiblePackages.map((incompatiblePkg) => (
                              <tr key={incompatiblePkg._id}>
                                <td>{incompatiblePkg.name || incompatiblePkg._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleRemoveIncompatiblePackage(incompatiblePkg._id, incompatiblePkg.name || 'this package')}
                                    title="Remove incompatible package"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No Incompatible Package Found
                      </div>
                    )}
                  </div>
                </div>

                {/* Incompatible Personalization Options Section */}
                <div className="package-details-tile" style={{ marginTop: '20px' }}>
                  <div className="package-details-tile-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <h3>Incompatible Personalization Options</h3>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchAvailableOptions();
                        setShowIncompatibleOptionsModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Option
                    </button>
                  </div>
                  <div className="package-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: If Homeowner purchases the current package, Personalization Options listed here will not be available to user.)
                    </p>
                    {pkg?.incompatiblePersonalizationOptions && pkg.incompatiblePersonalizationOptions.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Option Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pkg.incompatiblePersonalizationOptions.map((option) => (
                              <tr key={option._id}>
                                <td>{option.name || option._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleRemoveIncompatibleOption(option._id)}
                                    title="Remove incompatible option"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No Incompatible Personalization Options Found
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Models Tab */}
            {activeTab === 'models' && (
              <div className="package-details-tile">
                <div className="package-details-tile-header">
                  <h3>Models</h3>
                  <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowEditModelsModal(true)}
                      disabled={packageModels.length === 0}
                    >
                      <Pencil size={16} style={{ marginRight: '4px' }} />
                      Edit Model
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAddModelsModal(true)}
                    >
                      + Add Model
                    </button>
                  </div>
                </div>
                <div className="package-details-tile-content">
                  {loadingModels ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      Loading models...
                    </div>
                  ) : packageModels && packageModels.length > 0 ? (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Model Collection</th>
                            <th>Model Name</th>
                            <th>Type</th>
                            <th>Upgrade</th>
                            <th>Cost</th>
                            <th>Price</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {packageModels.map((model) => (
                            <tr key={model._id}>
                              <td>{model.modelCollection || '-'}</td>
                              <td>{model.modelName || '-'}</td>
                              <td>{model.modelType || '-'}</td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  backgroundColor: model.isUpgrade ? '#DEF7EC' : '#FEE2E2',
                                  color: model.isUpgrade ? '#03543F' : '#991B1B'
                                }}>
                                  {model.isUpgrade ? 'Upgrade' : 'Standard'}
                                </span>
                              </td>
                              <td>${model.cost?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td>${model.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#DC2626',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    marginLeft: 'auto'
                                  }}
                                  onClick={() => handleDeleteModel(model.modelId)}
                                  title="Remove model"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      No models added yet. Click "Add Model" to get started.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Units Tab */}
            {activeTab === 'units' && (
              <div className="package-details-tile">
                <div className="package-details-tile-header">
                  <h3>Units</h3>
                  <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowEditUnitsModal(true)}
                      disabled={packageUnits.length === 0}
                    >
                      <Pencil size={16} style={{ marginRight: '4px' }} />
                      Edit Unit
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAddUnitsModal(true)}
                    >
                      + Add Unit
                    </button>
                  </div>
                </div>
                <div className="package-details-tile-content">
                  {loadingUnits ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      Loading units...
                    </div>
                  ) : packageUnits && packageUnits.length > 0 ? (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Unit No</th>
                            <th>Floor</th>
                            <th>Model</th>
                            <th>Status</th>
                            <th>Upgrade</th>
                            <th>Cost</th>
                            <th>Price</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {packageUnits.map((unit) => (
                            <tr key={unit._id}>
                              <td>{unit.unitNo || '-'}</td>
                              <td>{unit.floorMarketing || '-'}</td>
                              <td>{unit.modelName || '-'}</td>
                              <td>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor:
                                    unit.status === 'AVAILABLE' ? '#DEF7EC' :
                                    unit.status === 'RESERVED' ? '#FEF3C7' :
                                    unit.status === 'SOLD' ? '#FEE2E2' : '#F3F4F6',
                                  color:
                                    unit.status === 'AVAILABLE' ? '#03543F' :
                                    unit.status === 'RESERVED' ? '#92400E' :
                                    unit.status === 'SOLD' ? '#991B1B' : '#6B7280'
                                }}>
                                  {unit.status || '-'}
                                </span>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  backgroundColor: unit.isUpgrade ? '#DEF7EC' : '#FEE2E2',
                                  color: unit.isUpgrade ? '#03543F' : '#991B1B'
                                }}>
                                  {unit.isUpgrade ? 'Upgrade' : 'Standard'}
                                </span>
                              </td>
                              <td>${unit.cost?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td>${unit.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#DC2626',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    marginLeft: 'auto'
                                  }}
                                  onClick={() => handleDeleteUnit(unit.unitId)}
                                  title="Remove unit"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      No units added yet. Click "Add Unit" to get started.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Purchased Units Tab */}
            {activeTab === 'purchased' && (
              <div className="package-details-tile">
                <div className="package-details-tile-content">
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Unit#</th>
                          <th>Unit Status</th>
                          <th>Deal#</th>
                          <th>Purchaser(s)</th>
                          <th>Deal Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingPurchasedUnits ? (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                              Loading purchased units...
                            </td>
                          </tr>
                        ) : purchasedUnits.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="data-table-empty">
                              No purchased units found for this package.
                            </td>
                          </tr>
                        ) : (
                          purchasedUnits.map((unit) => (
                            <tr key={unit.dealId}>
                              <td className="data-table-primary-cell">
                                <span
                                  onClick={() => window.open(`/workspaces/${workspaceId}/units?view=${unit.unitId}`, '_blank')}
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  title="View unit details"
                                >
                                  {unit.unitNo}
                                </span>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor: '#FEE2E2',
                                  color: '#991B1B'
                                }}>
                                  SOLD
                                </span>
                              </td>
                              <td>
                                <span
                                  onClick={() => window.open(`/workspaces/${workspaceId}/deals?view=${unit.dealId}`, '_blank')}
                                  style={{ cursor: 'pointer', textDecoration: 'underline', color: '#48182F' }}
                                  title="View deal details"
                                >
                                  {unit.dealNumber}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {unit.purchasers && unit.purchasers.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    {unit.purchasers.map((p, idx) => (
                                      <span
                                        key={idx}
                                        onClick={() => window.open(`/workspaces/${workspaceId}/contacts?view=${p.contactId}`, '_blank')}
                                        style={{
                                          fontWeight: p.isPrimary ? '600' : '400',
                                          cursor: 'pointer',
                                          textDecoration: 'underline'
                                        }}
                                        title="View purchaser details"
                                      >
                                        {p.displayName}
                                      </span>
                                    ))}
                                  </div>
                                ) : '-'}
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor:
                                    unit.dealStage === 'FIRM' ? '#DEF7EC' :
                                    unit.dealStage === 'RESERVED' ? '#FEF3C7' :
                                    unit.dealStage === 'UNIT RELEASED' ? '#DBEAFE' :
                                    unit.dealStage === 'SOLD' ? '#DEF7EC' : '#F3F4F6',
                                  color:
                                    unit.dealStage === 'FIRM' ? '#03543F' :
                                    unit.dealStage === 'RESERVED' ? '#92400E' :
                                    unit.dealStage === 'UNIT RELEASED' ? '#1E40AF' :
                                    unit.dealStage === 'SOLD' ? '#03543F' : '#6B7280'
                                }}>
                                  {unit.dealStage}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination and Records Per Page */}
                  {!loadingPurchasedUnits && purchasedUnits.length > 0 && (
                    <div className="contacts-pagination-controls" style={{ marginTop: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 8px' }}>
                      {/* Records Per Page Selector */}
                      <div className="contacts-records-per-page">
                        <span>Show</span>
                        <select
                          value={purchasedUnitsPagination.limit}
                          onChange={(e) => handlePurchasedUnitsLimitChange(Number(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span>records per page (Total: {purchasedUnitsPagination.total})</span>
                      </div>

                      {/* Pagination */}
                      {purchasedUnitsPagination.pages > 1 && (
                        <div className="contacts-pagination-wrapper">
                          <Pagination
                            current={purchasedUnitsPagination.page}
                            total={purchasedUnitsPagination.pages}
                            onPageChange={handlePurchasedUnitsPageChange}
                            maxWidth={400}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>

      {/* Footer */}
      <div className="package-details-footer">
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleClose}
        >
          Close
        </button>
      </div>

      {/* Add Spaces Modal */}
        <AddItemsModal
          isOpen={showAddSpacesModal}
          onClose={() => setShowAddSpacesModal(false)}
          onAdd={handleAddSpacesFromModal}
          availableOptions={[
            'Living Room',
            'Dining Room',
            'Kitchen',
            'Primary Bedroom',
            'Bedroom 2',
            'Bedroom 3',
            'Den',
            'Office',
            'Media Room',
            'Flex Room',
            'Powder Room',
            'Ensuite',
            'Main Bathroom',
            'Laundry Room',
            'Balcony',
            'Terrace',
            'Storage',
            'Foyer',
            'Hallway'
          ].filter(option => !spacesData.includes(option))}
          title="Add Spaces"
          customInputPlaceholder="Enter custom space name..."
        />

        {/* Add Color Collection Modal */}
        {showAddColorCollectionModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Color Collection</h3>
                <button className="modal-close-btn" onClick={() => {
                  setShowAddColorCollectionModal(false);
                  setSelectedCollections([]);
                }}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingColorCollections ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading collections...
                  </div>
                ) : (
                  <>
                    {selectedCollections.length > 0 && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#F5E6ED',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        fontSize: '13px',
                        color: '#48182F',
                        fontWeight: 500
                      }}>
                        {selectedCollections.length} collection(s) selected
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {colorCollections
                        .filter(collection => !pkg?.collections?.some(c => c.collectionId === collection._id))
                        .map(collection => {
                          const isSelected = selectedCollections.includes(collection._id);
                          return (
                            <div
                              key={collection._id}
                              onClick={() => handleToggleCollection(collection._id)}
                              style={{
                                padding: '12px 16px',
                                border: `2px solid ${isSelected ? '#48182F' : '#E5E7EB'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                backgroundColor: isSelected ? '#F5E6ED' : '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer',
                                  accentColor: '#48182F'
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                  {collection.name}
                                </div>
                                {collection.caption && (
                                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                    {collection.caption}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {colorCollections.filter(collection => !pkg?.collections?.some(c => c.collectionId === collection._id)).length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                          All available color collections have been added.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setShowAddColorCollectionModal(false);
                    setSelectedCollections([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  onClick={handleAddSelectedCollections}
                  disabled={selectedCollections.length === 0}
                >
                  Add Selected ({selectedCollections.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Models Modal */}
        <AddModelsModal
          isOpen={showAddModelsModal}
          workspaceId={workspaceId}
          projectId={pkg?.projectId || ''}
          onClose={() => setShowAddModelsModal(false)}
          onSave={handleAddModels}
          existingModels={packageModels.map(m => ({
            _id: m._id,
            colorCollectionId: packageId,
            modelId: m.modelId,
            modelName: m.modelName,
            modelCollection: m.modelCollection,
            modelType: m.modelType,
            isUpgrade: m.isUpgrade,
            cost: m.cost,
            price: m.price,
            projectId: pkg?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
        />

        {/* Add Units Modal */}
        <AddUnitsModal
          isOpen={showAddUnitsModal}
          workspaceId={workspaceId}
          projectId={pkg?.projectId || ''}
          onClose={() => setShowAddUnitsModal(false)}
          onSave={handleAddUnits}
          existingUnits={packageUnits.map(u => ({
            _id: u._id,
            colorCollectionId: packageId,
            unitId: u.unitId,
            unitNo: u.unitNo,
            floorMarketing: u.floorMarketing,
            modelName: u.modelName,
            status: u.status,
            isUpgrade: u.isUpgrade,
            cost: u.cost,
            price: u.price,
            projectId: pkg?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
        />

        {/* Edit Models Modal */}
        <AddModelsModal
          isOpen={showEditModelsModal}
          workspaceId={workspaceId}
          projectId={pkg?.projectId || ''}
          onClose={() => setShowEditModelsModal(false)}
          onSave={handleEditModels}
          existingModels={packageModels.map(m => ({
            _id: m._id,
            colorCollectionId: packageId,
            modelId: m.modelId,
            modelName: m.modelName,
            modelCollection: m.modelCollection,
            modelType: m.modelType,
            isUpgrade: m.isUpgrade,
            cost: m.cost,
            price: m.price,
            projectId: pkg?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
          editMode={true}
        />

        {/* Edit Units Modal */}
        <AddUnitsModal
          isOpen={showEditUnitsModal}
          workspaceId={workspaceId}
          projectId={pkg?.projectId || ''}
          onClose={() => setShowEditUnitsModal(false)}
          onSave={handleEditUnits}
          existingUnits={packageUnits.map(u => ({
            _id: u._id,
            colorCollectionId: packageId,
            unitId: u.unitId,
            unitNo: u.unitNo,
            floorMarketing: u.floorMarketing,
            modelName: u.modelName,
            status: u.status,
            isUpgrade: u.isUpgrade,
            cost: u.cost,
            price: u.price,
            projectId: pkg?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
          editMode={true}
        />

        {/* Parent Package Modal */}
        {showParentPackageModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Select Parent Package</h3>
                <button className="modal-close-btn" onClick={() => setShowParentPackageModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingPackages ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading packages...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availablePackages
                      .filter(p => !pkg?.parentPackage || p._id !== pkg.parentPackage.id)
                      .map(pkgItem => (
                        <div
                          key={pkgItem._id}
                          onClick={() => handleSetParentPackage(pkgItem._id)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: '#FFFFFF'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5E6ED';
                            e.currentTarget.style.borderColor = '#48182F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                            {pkgItem.name}
                          </div>
                          {pkgItem.caption && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {pkgItem.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    {availablePackages.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        No packages available in this project.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowParentPackageModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Incompatible Packages Modal */}
        {showIncompatiblePackagesModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Incompatible Package</h3>
                <button className="modal-close-btn" onClick={() => setShowIncompatiblePackagesModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingPackages ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading packages...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availablePackages
                      .filter(p => !pkg?.incompatiblePackages?.some(ip => ip._id === p._id))
                      .map(pkgItem => (
                        <div
                          key={pkgItem._id}
                          onClick={() => handleAddIncompatiblePackage(pkgItem._id)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: '#FFFFFF'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5E6ED';
                            e.currentTarget.style.borderColor = '#48182F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                            {pkgItem.name}
                          </div>
                          {pkgItem.caption && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {pkgItem.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    {availablePackages.filter(p => !pkg?.incompatiblePackages?.some(ip => ip._id === p._id)).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        All available packages have been added.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowIncompatiblePackagesModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Incompatible Options Modal */}
        {showIncompatibleOptionsModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Incompatible Personalization Option</h3>
                <button className="modal-close-btn" onClick={() => setShowIncompatibleOptionsModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingOptions ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading options...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availableOptions
                      .filter(o => !pkg?.incompatiblePersonalizationOptions?.some(io => io._id === o._id))
                      .map(option => (
                        <div
                          key={option._id}
                          onClick={() => handleAddIncompatibleOption(option._id)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: '#FFFFFF'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5E6ED';
                            e.currentTarget.style.borderColor = '#48182F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                            {option.name}
                          </div>
                          {option.caption && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {option.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    {availableOptions.filter(o => !pkg?.incompatiblePersonalizationOptions?.some(io => io._id === o._id)).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        All available options have been added.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowIncompatibleOptionsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Included Options Modal */}
        {showIncludedOptionsModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Personalization Option</h3>
                <button className="modal-close-btn" onClick={() => setShowIncludedOptionsModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingOptions ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading options...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availableOptions
                      .filter(o => !pkg?.includedOptions?.some(io => io._id === o._id))
                      .map(option => (
                        <div
                          key={option._id}
                          onClick={() => toggleIncludedOptionSelection(option._id)}
                          style={{
                            padding: '12px 16px',
                            border: selectedIncludedOptions.includes(option._id) ? '2px solid #48182F' : '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: selectedIncludedOptions.includes(option._id) ? '#F5E6ED' : '#FFFFFF',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIncludedOptions.includes(option._id)}
                            onChange={() => {}}
                            style={{
                              marginTop: '2px',
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              accentColor: '#48182F'
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                              {option.name}
                            </div>
                            {option.caption && (
                              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                {option.caption}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    {availableOptions.filter(o => !pkg?.includedOptions?.some(io => io._id === o._id)).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        All available options have been added.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setShowIncludedOptionsModal(false);
                    setSelectedIncludedOptions([]);
                  }}
                  disabled={addingIncludedOptions}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-primary btn-md ${addingIncludedOptions ? 'btn-loading' : ''}`}
                  onClick={handleAddIncludedOptions}
                  disabled={selectedIncludedOptions.length === 0 || addingIncludedOptions}
                >
                  {addingIncludedOptions ? '' : `Add ${selectedIncludedOptions.length > 0 ? `(${selectedIncludedOptions.length})` : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Delete Color Collection Dialog */}
        <ConfirmDialog
          isOpen={confirmDeleteCollection.show}
          title="Remove Color Collection"
          message={`Are you sure you want to remove "${confirmDeleteCollection.collectionName}" from this package?`}
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={confirmRemoveColorCollection}
          onClose={() => setConfirmDeleteCollection({ show: false, collectionId: null, collectionName: null })}
          confirmButtonStyle="danger"
        />

        {/* Confirm Delete Parent Package Dialog */}
        <ConfirmDialog
          isOpen={confirmDeleteParentPackage.show}
          title="Remove Parent Package"
          message={`Are you sure you want to remove the parent package?`}
          confirmText="OK"
          cancelText="Cancel"
          onConfirm={confirmRemoveParentPackage}
          onClose={() => setConfirmDeleteParentPackage({ show: false, packageName: null })}
          confirmButtonStyle="danger"
        />

        {/* Confirm Delete Incompatible Package Dialog */}
        <ConfirmDialog
          isOpen={confirmDeleteIncompatiblePackage.show}
          title="Remove Incompatible Package"
          message={`Are you sure you want to remove "${confirmDeleteIncompatiblePackage.packageName}" from incompatible packages?`}
          confirmText="OK"
          cancelText="Cancel"
          onConfirm={confirmRemoveIncompatiblePackage}
          onClose={() => setConfirmDeleteIncompatiblePackage({ show: false, packageId: null, packageName: null })}
          confirmButtonStyle="danger"
        />

        {/* General Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />

        {/* Change History FAB Button */}
        <ChangeHistoryButton
          onClick={() => setHistoryDrawerOpen(true)}
          isOpen={historyDrawerOpen}
        />

        {/* Change History Drawer */}
        <ChangeHistoryDrawer
          isOpen={historyDrawerOpen}
          onClose={() => setHistoryDrawerOpen(false)}
          workspaceId={workspaceId}
          entityType="package"
          entityId={packageId}
        />
      </div>
  );
};

export default PackageDetailsSlider;
